
import React, { useState, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent,
    Box, Typography, TextField, Button,
    FormControlLabel, Radio, CircularProgress, IconButton,
    InputAdornment,
} from '@mui/material';
import { Close as CloseIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';

const goldFieldSx = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: 'rgba(255,255,255,0.05)',
        '& fieldset': { borderColor: 'rgba(188, 150, 94, 0.57)' },
        '&:hover fieldset': { borderColor: 'rgba(188,150,94,0.5)' },
        '&.Mui-focused fieldset': { borderColor: '#bc965e' },
    },
    '& .MuiInputLabel-root': {
        color: 'rgba(255,255,255,0.7)',
        '&.Mui-focused': { color: '#bc965e' },
    },
    '& .MuiOutlinedInput-input': { color: 'white' },
    '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.35)', opacity: 1 },
};

const goldFieldErrorSx = {
    ...goldFieldSx,
    '& .MuiOutlinedInput-root': {
        ...goldFieldSx['& .MuiOutlinedInput-root'],
        '& fieldset': { borderColor: '#d32f2f' },
        '&:hover fieldset': { borderColor: '#d32f2f' },
        '&.Mui-focused fieldset': { borderColor: '#d32f2f' },
    },
};

const goldFieldDisabledSx = {
    ...goldFieldSx,
    '& .MuiOutlinedInput-root.Mui-disabled': {
        '& fieldset': { borderColor: 'rgba(188, 150, 94, 0.56)' },
    },
    '& .MuiOutlinedInput-input.Mui-disabled': {
        WebkitTextFillColor: 'rgba(255,255,255,0.55)',
    },
};

const EMPTY_FORM = {
    fullName: '', phoneNumber: '', address: '',
    city: '', state: '', pincode: '', isDefault: 0,
};

const AddAddressDialog = ({ open, onClose, onSave, saving }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [pinStatus, setPinStatus] = useState('idle'); // idle | checking | valid | invalid
    const debounceRef = useRef(null);

    const set = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const verifyPincode = async (pin) => {
        setPinStatus('checking');
        try {
            const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
            const data = await res.json();
            const result = data?.[0];
            const postOffice = result?.PostOffice?.[0];

            if (result?.Status === 'Success' && postOffice) {
                setForm(prev => ({
                    ...prev,
                    city: postOffice.District || postOffice.Block || prev.city,
                    state: postOffice.State || prev.state,
                }));
                setPinStatus('valid');
                setErrors(prev => ({ ...prev, pincode: '' }));
            } else {
                setPinStatus('invalid');
                setErrors(prev => ({ ...prev, pincode: 'This PIN code does not exist' }));
                setForm(prev => ({ ...prev, city: '', state: '' }));
            }
        } catch (err) {
            console.error('Pincode lookup failed:', err.message);
            // API unreachable — don't hard-block the user, but don't claim it's verified either
            setPinStatus('idle');
        }
    };

    const handlePincodeChange = (value) => {
        const digitsOnly = value.replace(/\D/g, '').slice(0, 6);
        set('pincode', digitsOnly);
        setForm(prev => ({ ...prev, city: '', state: '' }));
        setPinStatus('idle');

        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (/^[1-9]\d{5}$/.test(digitsOnly)) {
            debounceRef.current = setTimeout(() => verifyPincode(digitsOnly), 500);
        }
    };

    const validate = () => {
        const e = {};
        if (!form.fullName.trim()) e.fullName = 'Full name is required';
        if (!form.phoneNumber.trim()) e.phoneNumber = 'Phone number is required';
        else if (!/^[6-9]\d{9}$/.test(form.phoneNumber.trim())) e.phoneNumber = 'Enter a valid 10-digit Indian mobile number';
        if (!form.address.trim()) e.address = 'Address is required';
        if (!form.pincode.trim()) e.pincode = 'PIN code is required';
        else if (!/^[1-9]\d{5}$/.test(form.pincode.trim())) e.pincode = 'Enter a valid 6-digit PIN';
        else if (pinStatus === 'invalid') e.pincode = 'This PIN code does not exist';
        else if (pinStatus === 'checking') e.pincode = 'Please wait, verifying PIN code…';
        else if (pinStatus !== 'valid') e.pincode = 'Please enter a valid PIN code to auto-fill city/state';
        if (!form.city.trim()) e.city = 'City is required';
        if (!form.state.trim()) e.state = 'State is required';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = () => {
        if (validate()) onSave(form);
    };

    const handleClose = () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setForm(EMPTY_FORM);
        setErrors({});
        setPinStatus('idle');
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    background: '#1a1a1a',
                    border: '1px solid rgba(188, 150, 94, 0.8)',
                    borderRadius: 1,
                },
            }}
        >
            <DialogTitle sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                color: '#bc965e', fontWeight: 600, pb: 1,
                borderBottom: '1px solid rgba(188,150,94,0.15)',
            }}>
                Add a New Address
                <IconButton size="small" onClick={handleClose}
                    sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'white' } }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: '20px !important' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                    <TextField
                        fullWidth size="small" label="Full Name"
                        value={form.fullName}
                        onChange={e => set('fullName', e.target.value)}
                        error={!!errors.fullName} helperText={errors.fullName}
                        sx={errors.fullName ? goldFieldErrorSx : goldFieldSx}
                        FormHelperTextProps={{ sx: { color: '#d32f2f' } }}
                    />

                    <TextField
                        fullWidth size="small" label="Mobile Number"
                        value={form.phoneNumber}
                        onChange={e => set('phoneNumber', e.target.value)}
                        inputProps={{ maxLength: 10 }}
                        error={!!errors.phoneNumber} helperText={errors.phoneNumber}
                        sx={errors.phoneNumber ? goldFieldErrorSx : goldFieldSx}
                        FormHelperTextProps={{ sx: { color: '#d32f2f' } }}
                    />

                    <TextField
                        fullWidth size="small" label="Address (House No., Street, Area)"
                        value={form.address}
                        onChange={e => set('address', e.target.value)}
                        multiline rows={2}
                        error={!!errors.address} helperText={errors.address}
                        sx={errors.address ? goldFieldErrorSx : goldFieldSx}
                        FormHelperTextProps={{ sx: { color: '#d32f2f' } }}
                    />

                    <TextField
                        fullWidth size="small" label="PIN Code"
                        value={form.pincode}
                        onChange={e => handlePincodeChange(e.target.value)}
                        inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                        error={!!errors.pincode}
                        helperText={
                            errors.pincode
                                ? errors.pincode
                                : pinStatus === 'checking'
                                    ? 'Verifying PIN code…'
                                    : pinStatus === 'valid'
                                        ? 'PIN code verified'
                                        : 'City & State will auto-fill from PIN code'
                        }
                        FormHelperTextProps={{
                            sx: { color: errors.pincode ? '#d32f2f' : pinStatus === 'valid' ? '#4caf50' : 'rgba(255,255,255,0.5)' },
                        }}
                        sx={errors.pincode ? goldFieldErrorSx : goldFieldSx}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    {pinStatus === 'checking' && <CircularProgress size={16} sx={{ color: '#bc965e' }} />}
                                    {pinStatus === 'valid' && <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />}
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth size="small" label="City"
                        value={form.city}
                        InputProps={{ readOnly: true }}
                        placeholder="Auto-filled from PIN code"
                        error={!!errors.city} helperText={errors.city}
                        sx={errors.city ? goldFieldErrorSx : goldFieldDisabledSx}
                        FormHelperTextProps={{ sx: { color: '#d32f2f' } }}
                    />

                    <TextField
                        fullWidth size="small" label="State"
                        value={form.state}
                        InputProps={{ readOnly: true }}
                        placeholder="Auto-filled from PIN code"
                        error={!!errors.state} helperText={errors.state}
                        sx={errors.state ? goldFieldErrorSx : goldFieldDisabledSx}
                        FormHelperTextProps={{ sx: { color: '#d32f2f' } }}
                    />

                    <FormControlLabel
                        control={
                            <Radio
                                checked={!!form.isDefault}
                                onChange={e => set('isDefault', e.target.checked ? 1 : 0)}
                                size="small"
                                sx={{ color: 'rgba(188,150,94,0.5)', '&.Mui-checked': { color: '#bc965e' } }}
                            />
                        }
                        label={
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                Set as default address
                            </Typography>
                        }
                    />

                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                            variant="contained"
                            disabled={saving || pinStatus === 'checking'}
                            onClick={handleSave}
                            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                            className="glass-button"
                            sx={{ px: 3, textTransform: 'none' }}
                        >
                            {saving ? 'Saving…' : 'Save Address'}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            disabled={saving}
                            className="glass-button-outlined"
                            sx={{ textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                    </Box>

                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default AddAddressDialog;